"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useRefreshCart } from "@/hooks/use-refresh-cart";
import { useAuth } from "@/context/auth-context";
import { fetchAndSyncCouponDetails } from "@/hooks/use-fetch-coupons";
import { useCouponStore } from "@/lib/coupons/coupon-store";
import { clientAPI } from "@/lib/client-fetch";
import API_ENDPOINTS from "@/constants/api-endpoints";
import { freeTrialLabel } from "@/lib/calii-plus";
import { toast } from "sonner";

type Props = {
  onClose: () => void;
  freeTrialMonths: number;
  signupSource: string;
};

const CALII_PLUS_IMAGE_URL = "https://caliiorderissues.s3.us-east-2.amazonaws.com/calii_plus_1000.jpg";

export default function CaliiPlusModalContent({ onClose, freeTrialMonths, signupSource }: Props) {
  const router = useRouter();
  const refreshCart = useRefreshCart();
  const { refreshUser } = useAuth();
  const appliedCodes = useCouponStore((s) => s.appliedCodes);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const joinLabel =
    freeTrialMonths === 1 ? "Obtener 1 mes gratis" :
    freeTrialMonths > 1 ? `Obtener ${freeTrialMonths} meses gratis` :
    "Obtener membresía";

  const handleJoin = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    const result = await clientAPI.post(API_ENDPOINTS.CALII_PLUS_MEMBERSHIPS, {
      is_one_click_join: true,
      payment_method: "card",
      is_yearly: false,
      signup_source: signupSource,
    });
    if (result.ok) {
      await Promise.all([refreshCart(), refreshUser(), fetchAndSyncCouponDetails(appliedCodes)]);
      router.refresh();
      toast.success("Membresía Calii+ activada 🎉");
      onClose();
    } else {
      setSubmitError(result.error ?? "No se pudo iniciar tu membresía Calii+. Por favor vuelve a intentar o contacta a servicio al cliente.");
    }
    setIsSubmitting(false);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        {/* Header */}
        {/* TODO: header copy should come from API */}
        <div className="flex items-center gap-3 px-4 py-3">
          <button type="button" onClick={onClose} className="text-teal-500 shrink-0">
            <X className="h-5 w-5" />
          </button>
          <span className="font-semibold text-base">$1000 de regalo al mes y envíos gratis</span>
        </div>

        {/* Edge-to-edge image — hidden on very short viewports (< ~300px, i.e. <33% of typical 900px) */}
        {/* TODO: image URL should come from API */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={CALII_PLUS_IMAGE_URL}
          alt="Calii+ — $1000 de regalo y envíos gratis"
          className="w-full [@media(max-height:300px)]:hidden"
        />

        {/* Free trial banner */}
        {freeTrialMonths > 0 && (
          <div className="mx-4 mt-4 rounded-lg bg-teal-50 border border-teal-200 px-4 py-3 text-sm font-medium text-teal-700">
            🎁 Tu primera prueba incluye {freeTrialLabel(freeTrialMonths)}
          </div>
        )}

        {/* Body text */}
        {/* TODO: body copy (monthly credit amount, free delivery threshold, monthly price) should come from API */}
        <div className="px-4 pt-4 pb-4 space-y-3">
          <p className="text-sm">
            Con Calii+ recibes $1000* de regalo cada mes, envíos gratis ilimitados en pedidos mayores a $300, promos exclusivas y más.
          </p>
          <p className="text-sm text-muted-foreground">
            Al seleccionar el botón estás de acuerdo con los términos de Calii+ y el cobro automático mensual de $49.90 a la tarjeta asociada a tu cuenta hasta que canceles. Cancela en la app dentro de cuenta antes de cualquier renovación para evitar cargos.
          </p>
          <p className="text-sm text-muted-foreground">*Recibe hasta $250 de regalo en tus primeros 4 pedidos al mes mayores a $800 ($50 OFF), $1200 ($80 OFF), $1500 ($100 OFF), $2250 ($150 OFF), $3000 ($200 OFF) o $4000 ($250 OFF).</p>
        </div>
      </div>

      {/* Pinned footer */}
      <div className="border-t px-4 pt-4 pb-6 space-y-2 shrink-0">
        <Button
          className="w-full bg-teal-400 hover:bg-teal-500 cursor-pointer"
          size="lg"
          onClick={handleJoin}
          disabled={isSubmitting}
        >
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : joinLabel}
        </Button>
        <Button variant="outline" className="w-full cursor-pointer" size="lg">
          Aprender más
        </Button>
        <Button variant="outline" className="w-full cursor-pointer" size="lg" onClick={onClose}>
          Cerrar
        </Button>
      </div>

      {/* Error dialog */}
      <Dialog open={!!submitError} onOpenChange={(open) => { if (!open) setSubmitError(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>No pudimos activar Calii+</DialogTitle>
            <DialogDescription>{submitError}</DialogDescription>
          </DialogHeader>
          <Button className="w-full" onClick={() => setSubmitError(null)}>Cerrar</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
